import { ButtonBase } from '@mui/material';
import FeedbackRenderer from 'components/Feedback/FeedbackRenderer';
import PreviewGenerating from 'components/Grading/PreviewGenerating';
import { useViewport } from 'contexts/viewport';
import { memo, useEffect, useState } from 'react';
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Icon,
    IconButton,
    PDFViewer,
    ResponsiveDialog,
    SheetViewer,
    Stack,
    Text,
    styled,
} from 'shell/components';
import { getS3FileURL } from 'utils/helper';

const PreviewFeedbackDialog = memo(({ previewFeedbackDialog, onClose }) => {
    const { device } = useViewport();

    const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
    const [isDocumentFullScreen, setIsDocumentFullScreen] = useState(false);

    const getHighlights = () => {
        let highlights;
        highlights =
            previewFeedbackDialog.output?.data?.feedback?.feedback_type === 'rubric'
                ? previewFeedbackDialog.output?.data?.feedback?.improvement_suggestions?.find(
                      ({ id }) => id === selectedFeedbackId
                  )?.negative?.locations || []
                : previewFeedbackDialog.output?.data?.feedback?.improvement_suggestions?.find(
                      ({ id }) => id === selectedFeedbackId
                  )?.locations || [];
        return highlights.map((item) => ({ id: selectedFeedbackId, value: item }));
    };

    const handleClose = () => {
        onClose();
    };

    const renderFeedback = () => {
        if (!previewFeedbackDialog.output) return null;

        return (
            <FeedbackRenderer
                feedback={previewFeedbackDialog.output.data}
                selectedFeedbackId={selectedFeedbackId}
                setSelectedFeedbackId={setSelectedFeedbackId}
            />
        );
    };

    useEffect(() => {
        if (previewFeedbackDialog.output) {
            setSelectedFeedbackId(previewFeedbackDialog.output.data.feedback.improvement_suggestions?.[0]?.id);
        }
    }, [previewFeedbackDialog.output]);

    return (
        <CustomResponsiveDialog open={previewFeedbackDialog.open} scroll={'paper'} onClose={handleClose}>
            <DialogTitle>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={3}>
                        <Text component={'h1'} variant={'h6'} weight={'medium'}>
                            Preview Feedback Suggestions
                        </Text>
                    </Stack>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={handleClose}
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            <CustomDialogContent device={device}>
                {!previewFeedbackDialog.output ? (
                    <CustomStack alignItems={'center'} justifyContent={'center'}>
                        <PreviewGenerating />
                    </CustomStack>
                ) : (
                    <CustomStack
                        sx={device === 'desktop' && isDocumentFullScreen ? { overflow: 'hidden' } : undefined}
                        direction={device === 'desktop' ? 'row' : 'column'}
                    >
                        <FileStack device={device} isDocumentFullScreen={isDocumentFullScreen}>
                            {isDocumentFullScreen && (
                                <BannerBox>
                                    <Text color={'inherit'}>Currently viewing annotations for feedback.</Text>
                                </BannerBox>
                            )}
                            {previewFeedbackDialog.output?.file?.type === 'application/pdf' ? (
                                <PDFViewer
                                    highlights={getHighlights()}
                                    isFullScreen={isDocumentFullScreen}
                                    url={getS3FileURL(previewFeedbackDialog.output?.file)}
                                />
                            ) : (
                                <SheetViewer
                                    highlights={getHighlights()}
                                    url={getS3FileURL(previewFeedbackDialog.output?.file)}
                                />
                            )}
                        </FileStack>
                        <CenterButton device={device} onClick={() => setIsDocumentFullScreen((current) => !current)}>
                            <Stack
                                alignItems={'center'}
                                direction={device === 'desktop' ? 'column' : 'row-reverse'}
                                spacing={4}
                            >
                                <ArrowIcon
                                    kind={
                                        device === 'desktop'
                                            ? isDocumentFullScreen
                                                ? 'arrowLeft'
                                                : 'arrowRight'
                                            : isDocumentFullScreen
                                              ? 'arrowUp'
                                              : 'arrowDown'
                                    }
                                />
                                <CustomText device={device}>Hide</CustomText>
                            </Stack>
                        </CenterButton>
                        <FeedbackStack device={device} isDocumentFullScreen={isDocumentFullScreen}>
                            {renderFeedback()}
                        </FeedbackStack>
                    </CustomStack>
                )}
            </CustomDialogContent>
            <Divider />
            <DialogActions>
                <Stack
                    sx={{ width: '100%' }}
                    alignItems={'center'}
                    direction={'row-reverse'}
                    justifyContent={'space-between'}
                    spacing={4}
                >
                    <Button sx={{ flexShrink: 0 }} onClick={handleClose}>
                        Close Preview
                    </Button>
                    {previewFeedbackDialog.output && (
                        <Text color={'light'}>
                            Not looking quite right? Try adjusting the rubric, baseline or assignment description. You
                            can also add additional instructions in{' '}
                            <Text color={'inherit'} component={'i'}>
                                Advanced Settings
                            </Text>
                            .
                        </Text>
                    )}
                </Stack>
            </DialogActions>
        </CustomResponsiveDialog>
    );
});

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiDrawer-paper > .MuiBox-root': {
            height: '100%',
            padding: 0,
        },
        '& .MuiDialog-paper': {
            width: '100%',
            height: '100%',
        },
    };
});

const CustomDialogContent = styled(DialogContent)(({ theme, device }) => {
    return {
        padding: 0,
        background: theme.palette.ensightful.neutral[50],
        ...(device === 'mobile' && {
            height: 'calc(100% - 64px - 120px - 2px)',
        }),
    };
});

const CustomStack = styled(Stack)(() => {
    return {
        height: '100%',
    };
});

const FileStack = styled(Stack)(({ device, isDocumentFullScreen }) => {
    return {
        position: 'relative',
        ...(device === 'desktop'
            ? {
                  width: isDocumentFullScreen ? 'calc(100% - 24px)' : 'calc(60% - 12px)',
                  transition: 'width 150ms',
              }
            : {
                  height: isDocumentFullScreen ? 'calc(100% - 36px)' : 'calc(48% - 36px)',
                  transition: 'height 150ms',
              }),
    };
});

const BannerBox = styled(Box)(({ theme }) => {
    return {
        position: 'absolute',
        zIndex: 3,
        width: '100%',
        padding: theme.spacing(1, 5),
        background: theme.palette.ensightful.warning[200],
        color: theme.palette.ensightful.warning[900],
    };
});

const CenterButton = styled(ButtonBase)(({ theme, device }) => {
    return {
        background: theme.palette.ensightful.neutral[700],
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
            outlineOffset: '-5px',
        },
        '&:hover': {
            background: theme.palette.ensightful.neutral[800],
        },
        '&:active': {
            background: theme.palette.ensightful.neutral[900],
        },
        ...(device === 'desktop'
            ? {
                  width: '24px',
              }
            : {
                  height: '36px',
              }),
    };
});

const ArrowIcon = styled(Icon)(({ theme }) => {
    return {
        color: theme.palette.ensightful.shades[0],
    };
});

const CustomText = styled(Text)(({ theme, device }) => {
    return {
        width: 'max-content',
        color: theme.palette.ensightful.shades[0],
        ...(device === 'desktop' && {
            transform: 'rotate(-90deg)',
        }),
    };
});

const FeedbackStack = styled(Stack)(({ theme, device, isDocumentFullScreen }) => {
    return {
        ...(device === 'desktop'
            ? {
                  width: isDocumentFullScreen ? 0 : 'calc(40% - 12px)',
                  height: '100%',
                  transition: 'width 150ms',
              }
            : {
                  height: isDocumentFullScreen ? 0 : 'calc(52%)',
                  transition: 'height 150ms',
              }),
        background: theme.palette.ensightful.shades[0],
        overflow: 'auto',
    };
});

export default PreviewFeedbackDialog;
